import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Feather, Keyboard, Sparkles, Send, Plus, Loader2, X, Play, Pause } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";
import { toast } from "sonner";
import { singlishToSinhala, KB } from "@/lib/sinhala";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LB = {
  page_title: { si: "කවි කොන", en: "Poets' Corner", ta: "கவிதை மூலை" },
  subtitle: { si: "ඔබේ කවි මෙතැනින් ලියා එවන්න. සමාලෝචනයෙන් පසු මෙහි පළ වේ.", en: "Write and send your poem here. It appears below after review.", ta: "உங்கள் கவிதையை இங்கே எழுதி அனுப்புங்கள். சரிபார்ப்புக்குப் பிறகு இங்கே தோன்றும்." },
  write_btn: { si: "කවියක් ලියන්න", en: "Write a poem", ta: "கவிதை எழுது" },
  close: { si: "වසන්න", en: "Close", ta: "மூடு" },
  name_ph: { si: "ඔබේ නම", en: "Your name", ta: "உங்கள் பெயர்" },
  title_ph: { si: "කවියේ නම (අත්‍යවශ්‍ය නැත)", en: "Poem title (optional)", ta: "கவிதையின் தலைப்பு (விருப்பம்)" },
  singlish_label: { si: "English අකුරුවලින් type කර → සිංහල කර ගන්න", en: "Type in English letters → get Sinhala", ta: "ஆங்கில எழுத்துக்களில் தட்டச்சு → சிங்களம்" },
  singlish_ph: { si: "උදා: mata adare (→ මට අදරෙ)", en: "e.g. mata adare (→ මට අදරෙ)", ta: "எ.கா: mata adare" },
  add_btn: { si: "කවියට එකතු කරන්න", en: "Add to poem", ta: "கவிதையில் சேர்" },
  body_label: { si: "ඔබේ කවිය (සිංහල)", en: "Your poem (Sinhala)", ta: "உங்கள் கவிதை (சிங்களம்)" },
  body_ph: { si: "මෙතැන කවිය ලියන්න…", en: "Write your poem here…", ta: "இங்கே கவிதை எழுதுங்கள்…" },
  kb_toggle: { si: "අකුරු පුවරුව", en: "Letter keyboard", ta: "எழுத்து விசைப்பலகை" },
  polish_btn: { si: "AI මගින් සිංහල හදන්න", en: "Polish Sinhala with AI", ta: "AI மூலம் செம்மைப்படுத்து" },
  submit_btn: { si: "සමාලෝචනයට එවන්න", en: "Send for review", ta: "சரிபார்ப்புக்கு அனுப்பு" },
  wall_title: { si: "පළ කළ කවි", en: "Published poems", ta: "வெளியிடப்பட்ட கவிதைகள்" },
  empty: { si: "තවම කවි පළ කර නැත. පළමු කවිය ඔබෙන්ම වේවා! 🌸", en: "No poems yet. Be the first to share one! 🌸", ta: "இன்னும் கவிதைகள் இல்லை. முதலில் நீங்களே பகிருங்கள்! 🌸" },
  space: { si: "හිස්තැන", en: "Space", ta: "இடைவெளி" },
  back_btn: { si: "ආපසු", en: "Back", ta: "பின்" },
  folk: { si: "ජන කවි", en: "Folk poem", ta: "நாட்டுப்புறக் கவிதை" },
  listen: { si: "අහන්න", en: "Listen", ta: "கேளுங்கள்" },
  pause: { si: "නවත්වන්න", en: "Pause", ta: "இடைநிறுத்து" },
};

const PoemCard = ({ p, fontClass, L }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause();
    else a.play().catch(() => {});
  };
  return (
    <div className="rounded-3xl p-5" style={{ background: "var(--card)", border: "1px solid var(--border)" }} data-testid={`poem-card-${p.id}`}>
      {p.is_folk && (
        <span className="inline-block font-ui text-xs font-bold px-2.5 py-1 rounded-full mb-2" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>🪔 {L("folk")}</span>
      )}
      {p.title && <p className={`${fontClass()} text-lg font-bold`} style={{ color: "var(--text)" }}>{p.title}</p>}
      <p className={`${fontClass()} text-lg leading-loose mt-1 whitespace-pre-wrap`} style={{ color: "var(--text)" }}>{p.body}</p>
      {p.audio_url && (
        <>
          <button onClick={toggle} data-testid={`poem-play-${p.id}`}
            className="mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-full font-ui font-bold text-sm transition-transform active:scale-95"
            style={{ background: "var(--accent)", color: "#fff" }}>
            {playing ? <Pause size={18} /> : <Play size={18} />} {playing ? L("pause") : L("listen")}
          </button>
          <audio ref={audioRef} src={p.audio_url} preload="none" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
        </>
      )}
      {p.author_name && <p className="font-ui text-sm mt-3 text-right" style={{ color: "var(--accent)" }}>— {p.author_name}</p>}
    </div>
  );
};

export default function Poems() {
  const { lang, fontClass } = useLang();
  const L = (k) => (LB[k] && (LB[k][lang] || LB[k].en)) || k;

  const [poems, setPoems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [singlish, setSinglish] = useState("");
  const [body, setBody] = useState("");
  const [showKb, setShowKb] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  const load = () => axios.get(`${API}/poems`).then((r) => setPoems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const preview = singlishToSinhala(singlish);

  const insertAtCursor = (text) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + text); return; }
    const s = el.selectionStart ?? body.length;
    const e = el.selectionEnd ?? body.length;
    const next = body.slice(0, s) + text + body.slice(e);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = s + text.length;
      try { el.setSelectionRange(pos, pos); } catch (_) {}
    });
  };

  const addSinglish = () => {
    if (!preview.trim()) return;
    insertAtCursor((body && !body.endsWith(" ") && !body.endsWith("\n") ? " " : "") + preview);
    setSinglish("");
  };

  const polish = async () => {
    if (!body.trim()) { toast.error(L("body_ph")); return; }
    setPolishing(true);
    try {
      const { data } = await axios.post(`${API}/poems/polish`, { text: body });
      if (data?.text) { setBody(data.text); toast.success("✨"); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not polish, please try again.");
    } finally { setPolishing(false); }
  };

  const submit = async () => {
    if (body.trim().length < 2) { toast.error(L("body_ph")); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/poems`, { title, author_name: name, body });
      toast.success(data?.message || "Sent for review 🌸");
      setTitle(""); setSinglish(""); setBody(""); setOpen(false); setShowKb(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send, please try again.");
    } finally { setBusy(false); }
  };

  const KeyBtn = ({ ch, label }) => (
    <button type="button" onClick={() => insertAtCursor(ch)} data-testid={`poem-kb-${ch}`}
      className="h-11 min-w-[2.4rem] px-2 rounded-xl font-si text-lg transition-transform active:scale-90"
      style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}>
      {label || ch}
    </button>
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 pb-28">
        <Link to="/rooms/sindu-kamare" data-testid="poems-back" className="inline-flex items-center gap-2 font-ui text-sm font-bold mb-5" style={{ color: "var(--text-2)" }}>
          <ArrowLeft size={16} /> {L("back_btn")}
        </Link>

        <div className="text-center mb-6">
          <span className="grid place-items-center w-14 h-14 rounded-full mx-auto" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Feather size={26} /></span>
          <h1 className={`${fontClass()} text-3xl font-bold mt-3`} style={{ color: "var(--text)" }} data-testid="poems-title">{L("page_title")}</h1>
          <p className="font-ui text-sm mt-1" style={{ color: "var(--text-muted)" }}>{L("subtitle")}</p>
        </div>

        {!open && (
          <button onClick={() => setOpen(true)} data-testid="poems-write-btn"
            className="w-full h-14 rounded-full font-ui font-bold text-lg flex items-center justify-center gap-2 mb-8 transition-transform active:scale-95"
            style={{ background: "var(--accent)", color: "#fff" }}>
            <Feather size={20} /> {L("write_btn")}
          </button>
        )}

        {open && (
          <div className="rounded-3xl p-4 sm:p-5 mb-8 space-y-4" style={{ background: "var(--card)", border: "2px solid var(--border)" }} data-testid="poems-composer">
            <div className="flex justify-end">
              <button onClick={() => setOpen(false)} data-testid="poems-close" className="inline-flex items-center gap-1 font-ui text-xs font-bold" style={{ color: "var(--text-muted)" }}><X size={14} /> {L("close")}</button>
            </div>

            <input data-testid="poem-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={L("name_ph")}
              className="w-full h-12 px-4 rounded-2xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            <input data-testid="poem-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={L("title_ph")}
              className={`${fontClass()} w-full h-12 px-4 rounded-2xl outline-none`} style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />

            {/* Singlish helper */}
            <div className="rounded-2xl p-3" style={{ background: "var(--brass-light)", border: "1px solid var(--border)" }}>
              <p className="font-ui text-xs font-bold mb-2" style={{ color: "var(--accent)" }}>{L("singlish_label")}</p>
              <input data-testid="poem-singlish" value={singlish} onChange={(e) => setSinglish(e.target.value)} placeholder={L("singlish_ph")}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSinglish(); } }}
                className="w-full h-12 px-4 rounded-xl font-ui outline-none" style={{ background: "#fff", border: "1px solid var(--border)", color: "#1C140E" }} />
              {singlish && (
                <p className={`${fontClass()} text-lg mt-2 px-1`} style={{ color: "var(--text)" }} data-testid="poem-singlish-preview">→ {preview}</p>
              )}
              <button onClick={addSinglish} disabled={!preview.trim()} data-testid="poem-add-singlish"
                className="mt-2 h-10 px-4 rounded-full font-ui font-bold text-sm inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: "var(--accent)", color: "#fff" }}>
                <Plus size={15} /> {L("add_btn")}
              </button>
            </div>

            {/* Main body */}
            <div>
              <p className="font-ui text-xs font-bold mb-1.5" style={{ color: "var(--text-2)" }}>{L("body_label")}</p>
              <textarea ref={bodyRef} data-testid="poem-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder={L("body_ph")} rows={6}
                className={`${fontClass()} w-full px-4 py-3 rounded-2xl outline-none text-lg leading-relaxed resize-y`} style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            </div>

            <div className="flex flex-wrap gap-2">
              <button onClick={() => setShowKb((v) => !v)} data-testid="poem-kb-toggle"
                className="h-11 px-4 rounded-full font-ui font-bold text-sm inline-flex items-center gap-1.5" style={{ background: showKb ? "var(--accent)" : "var(--bg)", color: showKb ? "#fff" : "var(--text-2)", border: "1px solid var(--border)" }}>
                <Keyboard size={16} /> {L("kb_toggle")}
              </button>
              <button onClick={polish} disabled={polishing || body.trim().length < 2} data-testid="poem-polish"
                className="h-11 px-4 rounded-full font-ui font-bold text-sm inline-flex items-center gap-1.5 disabled:opacity-60" style={{ background: "var(--bg)", color: "var(--accent)", border: "1px solid var(--accent)" }}>
                {polishing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {L("polish_btn")}
              </button>
            </div>

            {showKb && (
              <div className="rounded-2xl p-3 space-y-2" style={{ background: "var(--bg)", border: "1px solid var(--border)" }} data-testid="poem-keyboard">
                <div className="flex flex-wrap gap-1.5">{KB.vowels.map((c) => <KeyBtn key={c} ch={c} />)}</div>
                <div className="flex flex-wrap gap-1.5">{KB.consonants.map((c) => <KeyBtn key={c} ch={c} />)}</div>
                <div className="flex flex-wrap gap-1.5">{KB.signs.map((c) => <KeyBtn key={c} ch={c} />)}</div>
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={() => insertAtCursor(" ")} className="h-11 px-5 rounded-xl font-ui text-sm" style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}>{L("space")}</button>
                  <button type="button" onClick={() => insertAtCursor("\n")} className="h-11 px-4 rounded-xl font-ui text-sm" style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}>↵</button>
                  <button type="button" onClick={() => setBody((b) => b.slice(0, -1))} data-testid="poem-kb-back" className="h-11 px-4 rounded-xl font-ui text-sm" style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}>⌫</button>
                </div>
              </div>
            )}

            <button onClick={submit} disabled={busy || body.trim().length < 2} data-testid="poem-submit"
              className="w-full h-14 rounded-full font-ui font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>
              {busy ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />} {L("submit_btn")}
            </button>
          </div>
        )}

        {/* Public wall */}
        <h2 className={`${fontClass()} text-xl font-bold mb-4`} style={{ color: "var(--text)" }} data-testid="poems-wall-title">{L("wall_title")}</h2>
        {poems.length === 0 ? (
          <p className="font-ui text-sm py-8 text-center" style={{ color: "var(--text-muted)" }} data-testid="poems-empty">{L("empty")}</p>
        ) : (
          <div className="space-y-4" data-testid="poems-wall">
            {poems.map((p) => (
              <PoemCard key={p.id} p={p} fontClass={fontClass} L={L} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
